<template>
  <v-container>
    <div class="mb-5 mt-3">
      <h1 class="headline font-weight-bold primary--text mb-2">Centro de Ayuda y Manuales</h1>
      <p class="subtitle-1 grey--text text--darken-1">Selecciona el manual correspondiente a tu rol para consultar las guías de uso de la plataforma.</p>
    </div>
    
    <v-layout row wrap>
      <v-flex xs12 sm6 md4 lg4 v-for="(manual, index) in availableManuals" :key="index" class="pa-2 d-flex">
        <v-card class="rounded-xl elevation-2 d-flex flex-column manual-card" style="width: 100%;">
          <!-- Colored header strip with floating icon -->
          <div class="card-header-strip" :class="'strip-' + manual.colorKey">
            <div class="icon-circle" :class="'icon-circle-' + manual.colorKey">
              <v-icon size="32" color="white">{{ manual.icon }}</v-icon>
            </div>
          </div>
          
          <!-- Content: icon pushes content down with pt-10 -->
          <v-card-text class="px-4 pt-10 pb-3 flex-grow-1 text-center">
            <h3 class="subtitle-1 font-weight-bold mb-2" style="line-height: 1.4; color: #2c3e50;">{{ manual.title }}</h3>
            <p class="caption grey--text text--darken-1 mb-0" style="line-height: 1.5;">{{ manual.description }}</p>
          </v-card-text>
          
          <v-card-actions class="px-4 pb-4">
            <v-btn 
              block
              depressed 
              :color="manual.color" 
              class="white--text font-weight-bold text-none"
              style="border-radius: 8px; height: 40px;"
              @click="openManual(manual)"
            >
              <v-icon left small>auto_stories</v-icon>
              Consultar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- PDF Viewer Dialog -->
    <v-dialog v-model="dialogPdf" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card style="display: flex; flex-direction: column; height: 100vh; background: #f0f2f5;">
        
        <!-- Toolbar -->
        <v-toolbar flat dark :color="selectedManual ? selectedManual.color : 'primary'" style="flex-shrink: 0;">
          <v-btn icon dark @click="dialogPdf = false">
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <v-toolbar-title class="font-weight-bold">
            {{ selectedManual ? selectedManual.title : 'Manual' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            v-if="numPages > 0"
            text
            dark
            class="text-none font-weight-bold"
            @click="printPdf"
          >
            <v-icon left>print</v-icon>
            Imprimir
          </v-btn>
          <v-btn
            v-if="srcPdfLink && srcPdfLink !== '#'"
            text
            dark
            class="text-none font-weight-bold"
            :href="srcPdfLink"
            target="_blank"
          >
            <v-icon left>open_in_new</v-icon>
            Abrir
          </v-btn>
        </v-toolbar>

        <!-- Scrollable content -->
        <div style="flex: 1 1 0; overflow-y: auto; display: flex; flex-direction: column; align-items: center; padding: 24px 16px;">
          
          <!-- Loading -->
          <div
            v-if="loadingPdf"
            style="display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; width: 100%;"
          >
            <v-progress-circular indeterminate color="primary" size="64" class="mb-4"></v-progress-circular>
            <span class="subtitle-1 grey--text">Cargando documento...</span>
          </div>

          <!-- Not available -->
          <div
            v-else-if="!srcPdfLink || srcPdfLink === '#'"
            style="display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; width: 100%;"
          >
            <v-icon size="88" color="grey lighten-2" class="mb-5">construction</v-icon>
            <h3 class="headline font-weight-bold grey--text text--darken-2 text-center">Documento en Construcción</h3>
            <p class="body-1 grey--text text--darken-1 mt-3 text-center" style="max-width: 380px;">
              Este manual se encuentra en proceso de elaboración y pronto estará disponible.
            </p>
            <v-btn depressed color="primary" class="mt-6 px-6" style="border-radius: 8px;" @click="dialogPdf = false">
              <v-icon left>arrow_back</v-icon>
              Volver
            </v-btn>
          </div>

          <!-- PDF Pages -->
          <template v-else>
            <!-- hidden ref for print -->
            <pdf ref="myPdfComponent" style="display: none;" :src="src"></pdf>
            <div
              v-for="i in numPages"
              :key="i"
              class="pdf-page-wrapper"
            >
              <pdf
                :src="src"
                :page="i"
                :style="{ width: '100%' }"
              ></pdf>
            </div>
          </template>

        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
    import pdf from 'vue-pdf'
    import store from "@/store"

    export default {
        name: "ayuda_admin",
        components: { pdf },
        data () {
            return {
                profileData: null,
                dialogPdf: false,
                selectedManual: null,
                src: null,
                srcPdfLink: null,
                numPages: 0,
                loadingPdf: false
            }
        },
        created () {
            this.getProfileData()
        },
        computed: {
            availableManuals() {
                if (!this.profileData) return []
                const roll = this.profileData.roll
                const isSupervisor = this.profileData.is_supervisor
                const isSuperuser = this.profileData.is_superuser
                const isShipmentsAnalyst = this.profileData.shipments_management
                const manuals = []

                if (roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Administrador General',
                        description: 'Uso, configuración y gestión integral de la plataforma.',
                        icon: 'admin_panel_settings',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/ManualAdministrador21.pdf',
                        color: 'primary', colorKey: 'blue'
                    })
                }
                if (roll === 4 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Administrador de Parque',
                        description: 'Control, administración y monitoreo de empresas del parque industrial.',
                        icon: 'location_city',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/Manual+Usuario+++Administrador+de+Parque+Industrial.pdf',
                        color: 'indigo', colorKey: 'indigo'
                    })
                }
                // if (roll === 5 || isSuperuser) {
                //     manuals.push({
                //         title: 'Administrador de Embarques',
                //         description: 'Configuración logística, asignación de andenes y tiempos de carga.',
                //         icon: 'local_shipping',
                //         link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+EMBARQUES.pdf',
                //         color: 'deep-orange darken-1', colorKey: 'deeporange'
                //     })
                //     manuals.push({
                //         title: 'Supervisor de Embarques',
                //         description: 'Gestión en piso, control de tiempos y liberación de cargas.',
                //         icon: 'assignment_turned_in',
                //         link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+SUPERVISOR+EMBARQUE.pdf',
                //         color: 'orange darken-2', colorKey: 'amber'
                //     })
                // }
                if (roll === 5 || isSuperuser || roll === 4) {
                    manuals.push({
                        title: 'Administrador de Parking',
                        description: 'Control y asignación de espacios, accesos y políticas de estacionamiento.',
                        icon: 'local_parking',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+PARKING.pdf',
                        color: 'blue darken-2', colorKey: 'cobalt'
                    })
                }
                if (isSupervisor || isShipmentsAnalyst || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Supervisor de Embarques',
                        description: 'Gestión en piso, control de tiempos y liberación de cargas.',
                        icon: 'assignment_turned_in',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+SUPERVISOR+EMBARQUE.pdf',
                        color: 'orange darken-2', colorKey: 'amber'
                    })
                    manuals.push({
                        title: 'Administrador de Embarques',
                        description: 'Configuración logística, asignación de andenes y tiempos de carga.',
                        icon: 'local_shipping',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+EMBARQUES.pdf',
                        color: 'deep-orange darken-1', colorKey: 'deeporange'
                    })
                }
                if (roll === 2 || roll === 3 || roll === 5 || isSuperuser || roll === 4 || roll === 9) {
                    manuals.push({
                        title: 'Vigilante y Caseta',
                        description: 'Escaneo de QR, control de accesos, visitantes y vehículos.',
                        icon: 'security',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+VIGILANTE.pdf',
                        color: 'blue-grey darken-1', colorKey: 'slate'
                    })
                }
                if (roll === 1 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Usuario Empleado',
                        description: 'Creación de invitaciones, reservación de accesos y perfil personal.',
                        icon: 'badge',
                        link: 'https://intrare-empresa-test.s3.us-east-2.amazonaws.com/manuales/ManualEmpleado21.pdf',
                        color: 'teal darken-1', colorKey: 'teal'
                    })
                }
                if (roll === 15 || roll === 11 || roll === 5 || isSuperuser) {
                    manuals.push({
                        title: 'Proveedores y Transportistas',
                        description: 'Pre-registro, agendamiento de visitas y acceso a instalaciones.',
                        icon: 'store',
                        link: 'https://intrare-empresa.s3.us-east-2.amazonaws.com/Handbook/MANUAL+DE+USUARIO+PROVEEDORES.pdf',
                        color: 'brown darken-1', colorKey: 'brown'
                    })
                }

                return manuals
            }
        },
        methods: {
            getProfileData () {
                axios({
                    method: 'get',
                    url: 'https://empresa-service.intrare.app/auth/me',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Token ' + store.getters.getToken
                    }
                }).then(response => {
                    this.profileData = response.data
                }).catch(error => {
                    console.log(error)
                })
            },
            openManual(manual) {
                this.selectedManual = manual
                this.srcPdfLink = manual.link
                this.src = null
                this.numPages = 0
                this.loadingPdf = false
                this.dialogPdf = true

                if (manual.link !== '#') {
                    this.loadingPdf = true
                    setTimeout(() => {
                        const task = pdf.createLoadingTask(manual.link)
                        this.src = task
                        task.promise.then(pdfObj => {
                            this.numPages = pdfObj.numPages
                            this.loadingPdf = false
                        }).catch(err => {
                            console.error('PDF error', err)
                            this.loadingPdf = false
                        })
                    }, 350)
                }
            },
            printPdf() {
                if (this.$refs.myPdfComponent) {
                    this.$refs.myPdfComponent.print()
                }
            }
        }
    }
</script>

<style scoped>
.manual-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #e0e0e0;
}
.manual-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 24px rgba(0,0,0,0.14) !important;
}

/* Colored banner at top */
.card-header-strip {
  height: 80px;
  border-radius: 12px 12px 0 0;
  position: relative;
  display: flex;
  justify-content: center;
}

/* Icon circle floats over the strip bottom edge */
.icon-circle {
  position: absolute;
  bottom: -30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 4px 14px rgba(0,0,0,0.22);
}

/* Strip gradients */
.strip-blue       { background: linear-gradient(135deg, #1565c0, #1e88e5); }
.icon-circle-blue { background: #1976D2; }

.strip-indigo       { background: linear-gradient(135deg, #283593, #3f51b5); }
.icon-circle-indigo { background: #3949AB; }

.strip-deeporange       { background: linear-gradient(135deg, #bf360c, #e64a19); }
.icon-circle-deeporange { background: #e64a19; }

.strip-cobalt       { background: linear-gradient(135deg, #0d47a1, #1565c0); }
.icon-circle-cobalt { background: #1565c0; }

.strip-amber       { background: linear-gradient(135deg, #e65100, #f57c00); }
.icon-circle-amber { background: #F57C00; }

.strip-slate       { background: linear-gradient(135deg, #37474f, #546e7a); }
.icon-circle-slate { background: #546E7A; }

.strip-teal       { background: linear-gradient(135deg, #004d40, #00897b); }
.icon-circle-teal { background: #00897B; }

.strip-brown       { background: linear-gradient(135deg, #4e342e, #6d4c41); }
.icon-circle-brown { background: #6D4C41; }

/* PDF page wrapper — lets vue-pdf canvas render full-width without clipping */
.pdf-page-wrapper {
  width: 100%;
  max-width: 860px;
  margin-bottom: 16px;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.12);
  background: white;
  overflow: visible; /* do NOT hide overflow — canvas must not clip */
}
/* Scale the internal canvas to fill the wrapper */
.pdf-page-wrapper canvas {
  width: 100% !important;
  height: auto !important;
  display: block;
}
</style>
